import { useEffect, useRef } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      remove: (widgetId: string) => void;
    };
  }
}

const TURNSTILE_SCRIPT =
  "https://challenges.cloudflare.com/turnstile/v0/api.js";
const SITE_KEY =
  (import.meta as ImportMeta & { env: Record<string, string> }).env
    .VITE_TURNSTILE_SITE_KEY ?? "";

let scriptLoaded: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (scriptLoaded) return scriptLoaded;
  scriptLoaded = new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = `${TURNSTILE_SCRIPT}?render=explicit`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("turnstile script load failed"));
    document.head.appendChild(s);
  });
  return scriptLoaded;
}

/**
 * Invisible Turnstile widget. onToken is fired when the challenge is passed
 * (usually without user interaction). If no site key is configured, this renders
 * nothing and immediately calls onToken with an empty string — the server will
 * also skip verification when no secret is set.
 */
export function Turnstile({ onToken }: { onToken: (token: string) => void }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!SITE_KEY) {
      onToken("");
      return;
    }
    let widgetId: string | null = null;
    let cancelled = false;
    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetId = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          size: "invisible",
          callback: (token: string) => onToken(token),
        });
      })
      .catch(() => {
        // If Turnstile fails to load, fall through without a token; server decides.
        onToken("");
      });
    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [onToken]);

  return <div ref={containerRef} />;
}
